import { useAppSettingStore } from '@/client/store/app-store';
import { api } from '@/client/utils/api';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

export type EventLabel = {
  /** Parent event id; present only when the market belongs to an event group. */
  eventId?: string;
  /** Event title (default text) for the primary line. */
  eventTitle?: string;
  /** Per-locale event title map (empty => fall back to eventTitle). */
  eventTitleI18n?: Record<string, string>;
  /** The sub-market's own per-locale name, for localizing the outcome name. */
  marketNameI18n?: Record<string, string>;
  /**
   * True only when the parent event groups more than one market (e.g. World Cup
   * Winner with a market per team). Single-market events are effectively
   * standalone questions, so callers should show just the title + direction and
   * omit the redundant outcome line.
   */
  multiMarket?: boolean;
};

const CACHE = { staleTime: Infinity, gcTime: Infinity } as const;

/**
 * Resolve marketAddress -> parent-event label (title + i18n) for portfolio /
 * trade / history / notification rows, which only carry a bare sub-market name
 * (e.g. "England") and no event context. Two cached stages, deduped app-wide by
 * react-query: getMarketDetail (address -> eventId + nameI18n) then
 * getEventDetail (eventId -> title + titleI18n). Unique markets/events are
 * fetched once and shared across every surface, so long lists stay cheap.
 *
 * Returns a map keyed by lower-cased marketAddress. A market with no parent
 * event yields an entry with only marketNameI18n set (eventTitle undefined),
 * so callers render the bare market name as today.
 */
export const useEventLabels = (marketAddresses: (string | undefined | null)[]): Record<string, EventLabel> => {
  const baseTokenAddress = useAppSettingStore(state => state.baseToken.address);

  // Stable dedup key so the memo/queries don't churn on every render even
  // though callers pass a fresh array literal each time.
  const marketsKey = marketAddresses
    .filter((a): a is string => !!a)
    .map(a => a.toLowerCase())
    .sort()
    .join(',');
  const uniqueMarkets = useMemo(
    () => Array.from(new Set(marketsKey ? marketsKey.split(',') : [])),
    [marketsKey]
  );

  const marketQueries = useQueries({
    queries: uniqueMarkets.map(addr => ({
      queryKey: ['EventLabel', 'market', addr],
      queryFn: async () => {
        const { data } = await api.market.getMarketDetail({ baseTokenAddress, marketAddress: addr });
        return data;
      },
      enabled: !!addr,
      ...CACHE,
    })),
  });

  const eventsKey = uniqueMarkets
    .map((_, i) => marketQueries[i]?.data?.eventId)
    .filter((id): id is string => !!id)
    .sort()
    .join(',');
  const uniqueEvents = useMemo(() => Array.from(new Set(eventsKey ? eventsKey.split(',') : [])), [eventsKey]);

  const eventQueries = useQueries({
    queries: uniqueEvents.map(id => ({
      queryKey: ['EventLabel', 'event', id],
      queryFn: async () => {
        const { data } = await api.market.getEventDetail({ id });
        return data;
      },
      enabled: !!id,
      ...CACHE,
    })),
  });

  const eventLabelById = useMemo(() => {
    const m: Record<string, { title?: string; titleI18n?: Record<string, string>; marketCount: number }> = {};
    uniqueEvents.forEach((id, i) => {
      const d = eventQueries[i]?.data;
      if (d) m[id] = { title: d.title, titleI18n: d.titleI18n, marketCount: d.markets?.length ?? 0 };
    });
    return m;
    // Rebuild only when the resolved event titles change.
  }, [uniqueEvents, eventQueries.map(q => q.data?.title).join('|')]);

  return useMemo(() => {
    const result: Record<string, EventLabel> = {};
    uniqueMarkets.forEach((addr, i) => {
      const md = marketQueries[i]?.data;
      const eid = md?.eventId || undefined;
      const ev = eid ? eventLabelById[eid] : undefined;
      result[addr] = {
        eventId: eid,
        eventTitle: ev?.title,
        eventTitleI18n: ev?.titleI18n,
        marketNameI18n: md?.nameI18n,
        multiMarket: (ev?.marketCount ?? 0) > 1,
      };
    });
    return result;
    // Dep signature must flip when a market's detail RESOLVES, not only when an
    // eventId appears — otherwise a standalone market (eventId always undefined)
    // never triggers a recompute and its marketNameI18n is dropped. Encode both
    // "data arrived" and the eventId + whether nameI18n is present.
  }, [
    uniqueMarkets,
    eventLabelById,
    marketQueries.map(q => (q.data ? `${q.data.eventId ?? ''}#${q.data.nameI18n ? '1' : '0'}` : '·')).join('|'),
  ]);
};
